import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { KozosCegService } from '../kozos-ceg.service';

/*  SZÁMLÁZÓ – az aktív cég (könyvelési ügyfél), a szerep rajta és az év.
 *
 *  SZEREP: 'ugyfel' – a felhasználó a cég nevében dolgozik (ír); 'konyvelo' – a könyvelőiroda
 *  szemével látja (csak olvas). A kiszolgáló dönti el (ugyfel-hozzaferes.js), itt csak megjelenítjük.
 *  A cég a többi modullal közös (KozosCegService): a Könyvelésből átlépve ugyanaz a cég nyílik. */
export interface SzamlazoAktiv {
  intezmeny: string;
  ugyfelId: string | null;
  ugyfelNev: string | null;
  szerep: 'ugyfel' | 'konyvelo' | null;
  ev: number;
}

@Injectable({ providedIn: 'root' })
export class SzamlazoService {
  aktiv$ = new BehaviorSubject<SzamlazoAktiv>({ intezmeny: '', ugyfelId: null, ugyfelNev: null, szerep: null, ev: new Date().getFullYear() });
  get aktiv(): SzamlazoAktiv { return this.aktiv$.value; }
  get ir(): boolean { return this.aktiv.szerep === 'ugyfel'; }
  private kesz = false;
  torzs: { afa_kulcsok: { kod: string; nev: string }[]; fizmodok: { kod: string; nev: string }[] } = { afa_kulcsok: [], fizmodok: [] };

  constructor(private http: HttpClient, private data: DataService, private kozos: KozosCegService) {}

  async init(): Promise<SzamlazoAktiv> {
    if (this.kesz) return this.aktiv;
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const m = (await this.data.getFromStorage('szamlazoAktiv')) || {};
    this.aktiv$.next({ intezmeny: si?.intezmenyId || '', ugyfelId: m.ugyfelId || null, ugyfelNev: m.ugyfelNev || null,
      szerep: m.szerep || null, ev: m.ev || new Date().getFullYear() });
    try { this.torzs = await this.get('/torzs'); } catch { /* a választók enélkül üresek */ }
    this.kesz = true;
    return this.aktiv;
  }
  async setAktiv(patch: Partial<SzamlazoAktiv>) {
    const a = { ...this.aktiv, ...patch };
    this.aktiv$.next(a);
    await this.data.setStorage('szamlazoAktiv', { ugyfelId: a.ugyfelId, ugyfelNev: a.ugyfelNev, szerep: a.szerep, ev: a.ev });
    if (a.ugyfelId) await this.kozos.jelol('SZAMLAZO', a.ugyfelId);
  }
  /** A látható cégek (szereppel) – az ügyfél csak a sajátjait, a könyvelő mindet. */
  ugyfelek(): Promise<any[]> { return this.get(`/ugyfelek/${this.aktiv.intezmeny}`); }
  /*  Modulba lépéskor: a másik modulban épp aktív cég (ha itt is látható), különben a legutóbbi;
   *  ha az sem látható már (pl. elvett kötés), az első látható cég. A szerep mindig frissül. */
  async kozosAtvesz(): Promise<void> {
    let lista: any[] = [];
    try { lista = await this.ugyfelek(); } catch { return; }
    const atv = await this.kozos.atveendo('SZAMLAZO');
    const u = (atv && lista.find((x) => x.id === atv)) || lista.find((x) => x.id === this.aktiv.ugyfelId)
      || lista.find((x) => x.szerep === 'ugyfel' && !x.inaktiv) || (lista.length === 1 ? lista[0] : null);
    if (u) await this.setAktiv({ ugyfelId: u.id, ugyfelNev: u.nev, szerep: u.szerep });
    else if (this.aktiv.ugyfelId) await this.setAktiv({ ugyfelId: null, ugyfelNev: null, szerep: null });
  }

  private url(p: string) { return `/api/szamlazo${p}`; }
  get<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.url(p))); }
  post<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.post<T>(this.url(p), body)); }
  put<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.put<T>(this.url(p), body)); }
  del<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.url(p))); }
  /** A bizonylat képe HTML-ben (nyugtánál a 80 mm-es blokk) – a közvetlen nyomtatáshoz. */
  html(id: string, szel = 80): Promise<string> { return firstValueFrom(this.http.get(this.url(`/szamla/${this.aktiv.ugyfelId}/${id}/pdf?html=1&szel=${szel}`), { responseType: 'text' })); }
  pdf(id: string, cim = 'Számla') { return this.data.megnyitFajl(this.url(`/szamla/${this.aktiv.ugyfelId}/${id}/pdf`), cim); }
}
